import { useInspectionLog } from '@/api/equipment/inspection-logs/getInspectionLog';
import { useRecordInspectionStep } from '@/api/equipment/inspection-logs/recordInspectionStep';
import { Button } from '@/components/ui/elements/button';
import { InspectionStepResult, ModelID } from '@/types';
import { useEffect, useRef, useState } from 'react';

import { Spinner } from '@/components/Elements';
import { CameraIcon, FolderOpenIcon, XMarkIcon } from '@heroicons/react/24/outline';

const MAX_FILES = 5;

interface InspectionLogStepPhotoManagerProps {
  inspectionLog: ReturnType<typeof useInspectionLog>['data'];
  stepId: ModelID;
  result?: InspectionStepResult;
}

export const InspectionLogStepPhotoManager = ({
  inspectionLog,
  stepId: _stepId,
  result,
}: InspectionLogStepPhotoManagerProps) => {
  const { mutate: logInspectionStepResult } = useRecordInspectionStep();
  const [error, setError] = useState<string | null>(null);
  const [showUploader, setShowUploader] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [files, setFiles] = useState<Array<{ file: File; preview: string }>>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const createdUrlsRef = useRef<string[]>([]);

  // Revoke all object URLs when the component unmounts
  useEffect(() => {
    return () => createdUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    if (selected.length === 0) return;

    const newEntries = selected.map((file) => {
      const preview = URL.createObjectURL(file);
      createdUrlsRef.current.push(preview);
      return { file, preview };
    });

    setFiles((prev) => [...prev, ...newEntries].slice(0, MAX_FILES));
    e.target.value = '';
    setError(null);
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const submitPhotos = () => {
    if (!inspectionLog || !inspectionLog.inspection) return;

    if (files.length === 0) {
      setError('Please select at least one photo.');
      return;
    }

    setError(null);
    setIsUploading(true);

    logInspectionStepResult(
      {
        inspection: inspectionLog.inspection,
        log: inspectionLog,
        inspection_step_id: _stepId,
        data: {
          status: result?.status,
          uploads: files.map(({ file }) => file),
        },
      },
      {
        onSuccess: () => {
          setFiles([]);
          setShowUploader(false);
        },
        onSettled: () => {
          setIsUploading(false);
        },
      },
    );
  };

  if (inspectionLog?.is_complete) {
    return (
      <div className="flex flex-row space-x-2 overflow-x-auto">
        <PhotoThumbnails result={result} />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-2">
        <div className="flex flex-row space-x-2 overflow-x-auto">
          <PhotoThumbnails result={result} />
          {isUploading && (
            <span className="text-sm bg-background/10 flex items-center justify-center size-16 rounded border">
              <Spinner />
            </span>
          )}
          <Button type="button" variant="outline" onClick={() => setShowUploader(!showUploader)}>
            <CameraIcon className="w-6 h-6" />
          </Button>
        </div>
      </div>

      {showUploader && (
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleFileChange}
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={handleFileChange}
          />

          <div className="flex flex-wrap gap-2 mb-3">
            {files.map(({ preview, file }, i) => (
              <div key={preview} className="relative">
                <img
                  src={preview}
                  alt={file.name}
                  className="h-16 w-16 object-cover rounded border border-gray-400"
                />
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  className="absolute -top-1 -right-1 bg-white rounded-full border border-gray-300 p-0.5"
                >
                  <XMarkIcon className="w-3 h-3 text-gray-600" />
                </button>
              </div>
            ))}
            {files.length < MAX_FILES && (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => cameraInputRef.current?.click()}
                  className="md:hidden h-16 w-16 flex flex-col items-center justify-center rounded border-2 border-dashed border-gray-300 text-gray-400 hover:border-gray-400 hover:text-gray-500 transition-colors"
                >
                  <CameraIcon className="w-5 h-5" />
                  <span className="text-[10px] mt-0.5">Camera</span>
                </button>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="h-16 w-16 flex flex-col items-center justify-center rounded border-2 border-dashed border-gray-300 text-gray-400 hover:border-gray-400 hover:text-gray-500 transition-colors"
                >
                  <FolderOpenIcon className="w-5 h-5" />
                  <span className="text-[10px] mt-0.5">Files</span>
                </button>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-1 mb-2">
            {error && <p className="text-sm text-red-600">{error}</p>}
            {isUploading && <p className="text-sm text-foreground/80">Uploading…</p>}
          </div>

          <Button type="button" onClick={submitPhotos} disabled={isUploading || files.length === 0}>
            {isUploading ? 'Uploading…' : 'Upload'}
          </Button>
        </div>
      )}
    </div>
  );
};

const PhotoThumbnails = ({ result }: { result: InspectionStepResult | undefined }) => {
  return (
    <>
      {result?.attachments?.map((attachment) => (
        <a
          key={attachment.id}
          href={attachment.download_link}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline"
        >
          <img
            src={attachment.download_link}
            alt={attachment.name}
            className="h-16 w-16 object-cover rounded border border-gray-400"
          />
        </a>
      ))}
    </>
  );
};
